/** @jsx jsx */
import { jsx, Container, Flex, Box, Styled, Button } from "theme-ui"
import { graphql, Link } from "gatsby"
import Img from "gatsby-image"
import { FaQuoteLeft } from "react-icons/fa"

import Layout from "../components/layout"
import SEO from "../components/seo"

import PageIntro from "../components/pageIntro"
import Checkmark from "../images/icons/checkmark.inline.svg"

const AboutPage = ({ data: { about, about: { testimonials: [testimonial] } } }) => (
  <Layout>
    <SEO title="About Us" />
    <section
      sx={{
        position: 'relative'
      }}>
      <Img
        fluid={about.hero.childImageSharp.fluid} />
      <PageIntro>
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <Box>
            <Styled.h1>
              <span sx={{ color: 'primary' }}>{about.title.split(" ")[0]}</span>
              {' '}
              <span sx={{ color: 'gray.6' }}>{about.title.split(" ")[1]}</span>
            </Styled.h1>
          </Box>
          <Box>
            <p>{about.intro}</p>
          </Box>
        </Flex>
      </PageIntro>
    </section>
    <section
      sx={{
        py: 3
      }}>
      <Container
        sx={{
          px: 4,
          position: 'relative',
        }}>
        <Flex
          sx={{
            justifyContent: 'center'
          }}>
          <Box
            sx={{
              mb: 4
            }}>
            <Styled.h3 sx={{ color: 'gray.5', textAlign: 'center' }}><em>{about.subtitle}</em></Styled.h3>
          </Box>
        </Flex>
      </Container>
    </section>
    { /** Services About */}
    <section
      sx={{
        py: 5
      }}>
      <Container
        sx={{
          px: 4,
          position: 'relative',
        }}>
        <Flex
          sx={{
            mx: -4,
            mb: 4,
            flexWrap: 'wrap'
          }}>
          {
            about.service.map(service => (
              <Flex
                key={service.id}
                sx={{
                  position: 'relative',
                  flexDirection: 'column',
                  pl: 5,
                  pr: 4,
                  mb: [5, 0],
                  width: ['full', '1/3']
                }}>
                <Checkmark sx={{ position: 'absolute', top: 0, left: 2, mt: 1, width: '35px' }} />
                <Box
                  sx={{
                    mb: 3
                  }}>
                  <Styled.h4>
                    <span sx={{ color: 'primary' }}>{service.title.slice(0, service.title.lastIndexOf(" "))}</span>
                    {' '}
                    <span sx={{ color: 'gray.6' }}>{service.title.split(" ").pop()}</span>
                  </Styled.h4>
                </Box>
                <Box
                  sx={{
                    mb: 4
                  }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: service.content
                    }} />
                </Box>
              </Flex>
            ))
          }
        </Flex>
        <Flex
          sx={{
            mx: -4,
            justifyContent: 'center'
          }}>
          <Box
            sx={{
              px: 4,
              ml: [0, -5]
            }}>
            <Button
              variant="primary">
              <Link
                sx={{
                  color: 'white'
                }}
                to="/contact">Get Started Today</Link>
            </Button>
          </Box>
        </Flex>
      </Container>
    </section>
    { /** Testimonial */}
    <section
      sx={{
        py: 5,
        bg: 'secondary'
      }}>
      <Container
        sx={{
          px: 4,
          position: 'relative',
        }}>
        <FaQuoteLeft
          sx={{
            position: 'absolute',
            color: 'white',
            opacity: 0.2,
            zIndex: 0
          }} width="100px" size="5em" />
        <Flex
          sx={{
            mx: -4,
            justifyContent: 'center'
          }}>
          <Box
            sx={{
              width: ['full', '3/4'],
              px: 4
            }}>
            <Flex
              sx={{
                flexWrap: 'wrap',
                justifyContent: ['center', 'flex-start'],
                mx: -4,
              }}>
              <Box
                sx={{
                  width: ['3/4', '1/4'],
                  px: 4
                }}>
                <Img fluid={testimonial.profile.childImageSharp.fluid} />
              </Box>
              <Box
                sx={{
                  width: ['full', '3/4'],
                  px: 4,
                  color: 'white'
                }}>
                <p>{testimonial.quote}</p>
                <p>- <strong>{testimonial.name}</strong>, {testimonial.title}</p>
              </Box>
            </Flex>
          </Box>
        </Flex>
      </Container>
    </section>
    { /** Our Values */}
    <section
      sx={{
        pt: 5,
        pb: 6
      }}>
      <Container
        sx={{
          px: 4,
          position: 'relative',
        }}>
        <Flex
          sx={{
            mx: -4,
            mb: 5,
            justifyContent: 'center'
          }}>
            <Box
              sx={{
                px: 4
              }}>
                <Styled.h2><span sx={{ color: 'primary' }}>Our</span> <span sx={{ color: 'gray.6' }}>Values</span></Styled.h2>
            </Box>
          </Flex>
        <Flex
          sx={{
            flexWrap: 'wrap',
            mx: -4,
          }}>
            {
              about.values.map(value => (
                <Box
                  key={ value.id }
                  sx={{
                    px: 4,
                    mb: [4, 0],
                    width: ['full', '1/3'],
                  }}>
                  <Flex
                    sx={{
                      bg: 'gray.2',
                      p: 4,
                      flexDirection: 'column',
                      height: 'full'
                    }} >
                      <Flex>
                        <Box
                          sx={{
                            width: ['1/3'],
                            textAlign: 'center'
                          }}>
                            <img src={value.icon.publicURL} sx={{ height: '65px' }} alt={ value.title } />
                        </Box>
                        <Box
                          sx={{
                            width: ['2/3']
                          }}><Styled.h4 sx={{ color: 'primary' }}>{ value.title }</Styled.h4></Box>
                      </Flex>
                      <Box>
                        <p>{ value.content }</p>
                      </Box>
                    </Flex>
                </Box>
              ))
            }
          </Flex>
      </Container>
    </section>
  </Layout>
)

export const query = graphql`
  query aboutPage {
    about: strapiAbout {
      hero {
        childImageSharp {
          fluid(maxWidth: 1800) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      id
      intro
      title
      subtitle
      service {
        title
        id
        content
      }
      values {
        content
        id
        title
        icon {
          publicURL
        }
      }
      testimonials {
        id
        name
        title
        quote
        profile {
          childImageSharp {
            fluid( maxWidth: 500){
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
    }
  } 
`

export default AboutPage
